import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBase } from '../components/ModalBase';
import { getOpenedSchema } from '../reducers/data/dataReducer';
import { getModelData, setModelData, getIsShowModelDataModal, setModelDataTestValue, getSchemaOrdering, setSchemaOrdering, getTestValue, getIsProcessing } from '../reducers/modelData/modelDataReducer';
import { closeModelDataModalAction, saveModelDataModalAction } from '../sagas/modelDataSaga/modelDataSagaActions';
import CodeMirror, { ViewUpdate } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { CodeMirrorStyles, CodeMirrorTheme, FlexBoxInline, InputBaseSx, LinkSxSmall, PrimaryBtnBorderlessSx, SecondaryBtnSx, TooltipSlotProps } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ContextHelper } from '../components/ContextHelper';
import { dataToJson, parseFileData } from '../@shared/schemaDataParser';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import { GuideStepEnum, NotificationModel, NotificationModelType } from '../reducers/main/mainModel';
import { filterHierarchy, getDefaultValueForSchemaItem, getSchemaNamesDictionary } from '../@shared/schemaUtils';
import { StructureColumnTypeEnum } from '../@shared/Models/Models';
import GuideCallout from '../components/GuideCallout';
import { getGuideStep, setGuideStep } from '../reducers/main/mainReducer';

export const withAddOrUpdateModelData = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const dispatch = useDispatch();
  const handleClose = () => dispatch(closeModelDataModalAction())
  const onSave = () => {
    dispatch(saveModelDataModalAction())
  }
  const data = useSelector(getModelData);
  const isProcessing = useSelector(getIsProcessing);
  const schemaOrdering = useSelector(getSchemaOrdering);
  const testValue = useSelector(getTestValue);
  const schema = useSelector(getOpenedSchema);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const guideStep = useSelector(getGuideStep);

  const flattenedSchemaItems = React.useMemo(() => {
    if (!schema?.schemaItems) return [];
    const filtered = filterHierarchy(schema.schemaItems, [StructureColumnTypeEnum.Feature, StructureColumnTypeEnum.Info]);
    return getSchemaNamesDictionary(filtered)
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [schema]);

  const structure = React.useMemo(() => {
    const valueItem: unknown[] = [];
    flattenedSchemaItems.forEach(({ schemaItem }) => {
      valueItem.push(getDefaultValueForSchemaItem(schemaItem));
    });
    return [valueItem];
  }, [flattenedSchemaItems])

  const baseOrder = React.useMemo(() => {
    return flattenedSchemaItems.map(x => x.key).join(", ");
  }, [flattenedSchemaItems])

  const onChangeData = (value: string, _: ViewUpdate) => {
    dispatch(setModelData(value))
  }

  const onChangeTestPercentage = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isNaN(Number(e.target.value)) && Number(e.target.value) >= 0 && Number(e.target.value) <= 100) {
      dispatch(setModelDataTestValue(Number(e.target.value)))
    }
  }

  const isOpen = useSelector(getIsShowModelDataModal);

  const onCopyBaseOrder = React.useCallback(async () => {
    navigator.clipboard.writeText(baseOrder);
  }, [baseOrder]);

  const handleLoadFile = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = e.target?.result as string;
      if (fileContent && flattenedSchemaItems.length > 0) {
        const schemaItemsForParsing = flattenedSchemaItems.map(f => ({
          ...f.schemaItem,
          name: f.key
        }));
        const result = parseFileData(fileContent, schemaItemsForParsing);

        if (result.isValid) {
          dispatch(setModelData(dataToJson(result.data, true)));
          dispatch(setSchemaOrdering(result.schemaOrdering.join(",")));
        } else {
          dispatch(onThrowAlert({
            message: result.error || "Failed to parse file. Please ensure it's a valid CSV or JSON schema file.",
            lifeTime: 15000,
            type: NotificationModelType.Error
          } as NotificationModel));
        }
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }, [dispatch, flattenedSchemaItems]);

  React.useEffect(() => {
    if (isOpen) {
      dispatch(setModelData(JSON.stringify(structure, null, 2)))
      dispatch(setSchemaOrdering(baseOrder))
      dispatch(setModelDataTestValue(30))
    }
  }, [baseOrder, dispatch, isOpen, structure])

    const fillExample = () => {
      const exampleData = [
        [540,52,0,0,"pro","US","no"],[12,3,4,2,"free","US","yes"],[320,41,1,0,"pro","DE","no"],
        [45,6,3,1,"free","FR","yes"],[700,61,0,0,"pro","US","no"],[90,11,2,1,"free","Other","yes"],
        [410,38,1,0,"pro","UK","no"],[18,2,5,3,"free","Other","yes"],[250,29,1,0,"pro","Other","no"],
        [35,4,4,2,"free","US","yes"],[610,58,0,0,"pro","DE","no"],[22,5,4,2,"free","FR","yes"],
        [480,44,1,0,"pro","UK","no"],[65,9,3,1,"free","Other","yes"],[530,49,0,0,"pro","Other","no"],
        [40,7,3,1,"free","Other","yes"],[390,35,1,0,"pro","US","no"],[28,4,5,2,"free","DE","yes"],
        [720,63,0,0,"pro","UK","no"],[55,8,4,1,"free","FR","yes"],[460,42,1,0,"pro","US","no"],
        [16,2,5,3,"free","Other","yes"],[340,31,1,0,"pro","DE","no"],[75,10,3,1,"free","Other","yes"],
        [580,55,0,0,"pro","UK","no"],[25,3,4,2,"free","Other","yes"],[430,39,1,0,"pro","Other","no"],
        [48,6,3,1,"free","US","yes"],[670,60,0,0,"pro","DE","no"],[32,5,4,2,"free","FR","yes"],
        [510,47,1,0,"pro","UK","no"],[80,12,2,1,"free","Other","yes"],[390,36,1,0,"pro","US","no"],
        [14,1,5,3,"free","Other","yes"],[620,57,0,0,"pro","Other","no"],[38,5,4,2,"free","DE","yes"],
        [470,43,1,0,"pro","FR","no"],[58,7,3,1,"free","US","yes"],[560,53,0,0,"pro","UK","no"],
        [26,4,4,2,"free","Other","yes"],[360,33,1,0,"pro","Other","no"],[70,9,3,1,"free","Other","yes"],
        [640,59,0,0,"pro","US","no"],[20,3,5,2,"free","DE","yes"],[450,41,1,0,"pro","FR","no"],
        [52,7,3,1,"free","UK","yes"],[590,56,0,0,"pro","Other","no"],[30,4,4,2,"free","Other","yes"],
        [400,37,1,0,"pro","Other","no"],[60,8,3,1,"free","US","yes"],[550,51,0,0,"pro","DE","no"],
        [24,3,5,2,"free","FR","yes"],[370,34,1,0,"pro","UK","no"],[68,10,2,1,"free","Other","yes"],
        [600,57,0,0,"pro","Other","no"],[27,4,4,2,"free","Other","yes"],[420,38,1,0,"pro","US","no"],
        [50,6,3,1,"free","DE","yes"],[630,58,0,0,"pro","FR","no"],[19,2,5,3,"free","UK","yes"],
        [350,32,1,0,"pro","Other","no"],[72,11,2,1,"free","Other","yes"],[570,54,0,0,"pro","Other","no"],
        [29,4,4,2,"free","US","yes"],[440,40,1,0,"pro","DE","no"],[54,7,3,1,"free","FR","yes"],
        [610,56,0,0,"pro","UK","no"],[21,3,5,2,"free","Other","yes"],[330,30,1,0,"pro","Other","no"],
        [77,10,2,1,"free","Other","yes"],[520,48,0,0,"pro","US","no"],[36,5,4,2,"free","DE","yes"],
        [460,42,1,0,"pro","FR","no"],[62,8,3,1,"free","UK","yes"],[680,61,0,0,"pro","Other","no"],
        [15,2,5,3,"free","Other","yes"],[380,35,1,0,"pro","Other","no"],[66,9,3,1,"free","US","yes"],
        [540,50,0,0,"pro","DE","no"],[31,4,4,2,"free","FR","yes"],[410,37,1,0,"pro","UK","no"],
        [57,7,3,1,"free","Other","yes"],[650,59,0,0,"pro","Other","no"],[23,3,5,2,"free","Other","yes"],
        [340,31,1,0,"pro","US","no"],[73,11,2,1,"free","DE","yes"],[560,52,0,0,"pro","FR","no"],
        [34,5,4,2,"free","UK","yes"],[430,39,1,0,"pro","Other","no"],[64,8,3,1,"free","Other","yes"],
        [690,62,0,0,"pro","Other","no"],[17,2,5,3,"free","US","yes"],[390,36,1,0,"pro","DE","no"],
        [59,7,3,1,"free","FR","yes"],[580,55,0,0,"pro","UK","no"],[33,4,4,2,"free","Other","yes"],
        [450,41,1,0,"pro","Other","no"],[69,10,2,1,"free","Other","yes"],[620,58,0,0,"pro","US","no"],
        [26,3,5,2,"free","DE","yes"],[360,33,1,0,"pro","FR","no"],[75,11,2,1,"free","UK","yes"],
        [530,49,0,0,"pro","Other","no"],[39,5,4,2,"free","Other","yes"],[470,43,1,0,"pro","Other","no"],
        [61,8,3,1,"free","US","yes"],[640,60,0,0,"pro","DE","no"],[18,2,5,3,"free","FR","yes"],
        [400,37,1,0,"pro","UK","no"],[56,7,3,1,"free","Other","yes"],[600,57,0,0,"pro","Other","no"],
        [28,4,4,2,"free","Other","yes"],[420,38,1,0,"pro","US","no"],[63,9,3,1,"free","DE","yes"],
        [660,61,0,0,"pro","FR","no"],[20,3,5,2,"free","UK","yes"],[370,34,1,0,"pro","Other","no"],
        [71,10,2,1,"free","Other","yes"],[550,53,0,0,"pro","Other","no"],[35,5,4,2,"free","US","yes"],
        [440,40,1,0,"pro","DE","no"],[58,8,3,1,"free","FR","yes"],[610,58,0,0,"pro","UK","no"],
        [22,3,5,2,"free","Other","yes"],[350,32,1,0,"pro","Other","no"],[74,11,2,1,"free","Other","yes"],
        [520,48,0,0,"pro","US","no"],[37,5,4,2,"free","DE","yes"],[460,42,1,0,"pro","FR","no"],
        [65,9,3,1,"free","UK","yes"],[670,60,0,0,"pro","Other","no"],[16,2,5,3,"free","Other","yes"],
        [390,35,1,0,"pro","Other","no"],[60,8,3,1,"free","US","yes"],[570,54,0,0,"pro","DE","no"],
        [29,4,4,2,"free","FR","yes"],[430,39,1,0,"pro","UK","no"],[67,10,2,1,"free","Other","yes"],
        [630,59,0,0,"pro","Other","no"],[24,3,5,2,"free","Other","yes"],[340,30,1,0,"pro","US","no"],
        [72,11,2,1,"free","DE","yes"],[560,52,0,0,"pro","FR","no"],[32,5,4,2,"free","UK","yes"],
        [410,37,1,0,"pro","Other","no"],[55,7,3,1,"free","Other","yes"],[650,60,0,0,"pro","Other","no"],
        [19,2,5,3,"free","US","yes"],[380,35,1,0,"pro","DE","no"],[62,8,3,1,"free","FR","yes"],
        [590,56,0,0,"pro","UK","no"],[27,4,4,2,"free","Other","yes"],[450,41,1,0,"pro","Other","no"],
        [70,10,2,1,"free","Other","yes"]
      ];
      dispatch(setSchemaOrdering("accountAgeDays, monthlyUsageHours, supportTickets, paymentFailures, subscriptionType, country, churned"));
      dispatch(setModelData(JSON.stringify(exampleData, null, 2)));
      dispatch(setModelDataTestValue(30));
      dispatch(setGuideStep(GuideStepEnum.SaveData));
    }
  

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={onSave}
      title="Collect data"
      isProcessing={isProcessing}
      isDisabledSave={isProcessing}
      saveBtnTooltip={guideStep === GuideStepEnum.SaveData ? <GuideCallout>Click to save your data.</GuideCallout> : undefined}
    >
      <Box sx={{ marginBottom: "10px" }}>
        <ContextHelper boxSx={{ marginBottom: "5px" }} content={<>The list of item names that define the order of values in each data row. It ensures that every value in data matches the correct feature, target, or info field.</>}>
          <Tooltip placement="left-start" open={guideStep === GuideStepEnum.FillDatasetModal} slotProps={TooltipSlotProps} title={
            <GuideCallout>
              Click to fill the form with example data.
              <br />
              <Button
                  sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }}
                  onClick={() => {
                    fillExample();
                  }}>
                  Example
              </Button>
            </GuideCallout>
          } arrow>
            <TextField sx={{ ...InputBaseSx }} value={schemaOrdering} onChange={(e) => dispatch(setSchemaOrdering(e.target.value))} fullWidth className='' id="Schema-Ordering-field" size="small" label="Schema Ordering" variant="outlined" />
          </Tooltip>
        </ContextHelper>
        <Box sx={{ ...FlexBoxInline, marginBottom: "10px" }}>
          <Typography sx={{
            color: Palette.textColorSecondary,
            fontSize: Sizes.textSecondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            Basic order: {baseOrder}
          </Typography>
          <Tooltip title={"Copy"} placement="bottom" arrow>
            <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="copy" onClick={onCopyBaseOrder}>
              <ContentCopyIcon sx={{ fontSize: Sizes.textPrimary }} />
            </IconButton>
          </Tooltip>
        </Box>
        <ContextHelper content={<>The percentage of the dataset that will be reserved for testing the model after training. For example, 20 means 20% of the data will be used for testing and 80% for training.</>}>
          <TextField
            sx={{ ...InputBaseSx, marginBottom: "10px", width: "100px" }}
            size="small"
            value={testValue}
            onChange={onChangeTestPercentage}
            label="Test"
            variant="outlined"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }
            }} />
        </ContextHelper>

        <Box sx={{ ...FlexBoxInline, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "10px" }}>
          <ContextHelper content={<>The dataset must be provided as a JSON array of rows. Each row is an array of values, and the values must follow the order defined in Schema Ordering — the first value corresponds to the first field name, the second value to the second field name, and so on.</>}>
            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Data:</Typography>
          </ContextHelper>
          <Tooltip title={"Upload CSV or JSON file"} placement="bottom" arrow>
            <IconButton sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }} size="medium" onClick={handleLoadFile}>
              <UploadFileIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>

        <input
          type="file"
          ref={fileInputRef}
          accept=".csv,text/csv,.json,application/json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          aria-label="Upload CSV or JSON file"
        />
        <CodeMirror value={data} maxHeight="400px" extensions={[json()]} onChange={onChangeData} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
      </Box>


    </ModalBase>
    <Component {...props as T} />
  </>
}